import {Box, Grid} from '@mui/material';
import React, {useMemo} from 'react';
import {Link} from 'react-router-dom';
import {DataGridPro} from '@mui/x-data-grid-pro';
import clsx from 'clsx';
import {SearchResults} from '../Common/SearchResults';
import {useAccountContext} from '../../contexts/AccountContext';
import {Pot} from '../../schema-types/Pot';
import {isBankAccount} from '../../utils/potsFilter';
import {getEnvironment} from '../../utils/envTest';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import useDjango from '../../hooks/useDjango';
import {GlobalPermissionEnum} from '../../schema-types/globalTypes';
import {useDjangoContext} from '../../contexts/useAuthContext';
import {PotDetails} from '../../schema-types/PotDetails';
import {getCurrencyString} from '../../utils/currencyFormatter';
import {SaltusAny} from '../../utils/SaltusAny';
import {Panel} from '../Panel/Panel';
import Loading from '../Loading/Loading';
import {themeColors} from '../../theme/Theme';
import {Button} from '../Button/Button';
import {useAccountPlanColumns} from './useAccountPlanColumns';
import {useRequirementsDentistOrBAU} from '../../hooks/useRequirementsDentistOrBAU';

const ListSubplans: React.FC<{subplans: PotDetails[]; parent: Pot}> = ({
  subplans,
  parent,
}) => {
  const crys: SaltusAny[] = [];
  const {djangoUser} = useDjangoContext();
  const {accountPlanColumns, accountPlanColumnsWithDelete} = useAccountPlanColumns();
  const planNeedsCrystallised = parent.productName?.formattedName
    ?.toLocaleLowerCase()
    .includes('sipp-drawdown');

  if (planNeedsCrystallised) {
    crys.push(
      {
        id: `crystallisedFunds`,
        totalValue: getCurrencyString(parent.crystallisedFunds),
        productName: {formattedName: 'Crystallised'},
        __typename: 'PotType',
      } as Pot,
      {
        id: `uncrystallisedFunds`,
        totalValue: getCurrencyString(parent.uncrystallisedFunds),
        productName: {formattedName: 'Uncrystallised'},
        __typename: 'PotType',
      } as Pot
    );
  }

  return (
    <DataGridPro
      className={clsx('root', 'subplans')}
      slots={{
        columnHeaders: () => null,
      }}
      rows={crys}
      getRowClassName={() => 'child'}
      columns={
        djangoUser?.isUserDev || djangoUser?.isUserTester
          ? accountPlanColumnsWithDelete
          : accountPlanColumns
      }
      getDetailPanelContent={() => null}
      hideFooter
      autoHeight={false}
      sx={{
        maxHeight: 84,
        height: 84,
        overflowY: 'hidden',
        '& ::-webkit-scrollbar': {display: 'none'},
        '& *': {
          textTransform: 'capitalize',
          textAlign: 'left',
          '-ms-overflow-style': 'none',
        },
      }}
      rowHeight={40}
    />
  );
};

export const AccountPlans: React.FC = () => {
  const {accountId, potsLoading, pots} = useAccountContext();
  const {recommendsGridUrl} = useRequirementsDentistOrBAU();
  const {djangoUser} = useDjango();
  const filteredPlans = pots.filter((x) => {
    return !!x.productName?.formattedName && !isBankAccount(x) && !!x.curoId;
  });

  const plans = filteredPlans.reduce((acc, curr) => {
    acc.push(curr);
    if (curr.children?.length) {
      curr.children.forEach((child) => {
        acc.push(child);
      });
    }
    return acc;
  }, [] as PotDetails[]);
  const envName = useMemo(() => {
    return getEnvironment();
  }, []);
  const {accountPlanColumnsWithDelete, accountPlanColumns} = useAccountPlanColumns();

  if (potsLoading) {
    return (
      <Grid item xs={12}>
        <Panel color={themeColors.lightBlue} title="Active plans">
          <Loading entityName="plans" />
        </Panel>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <SearchResults
        dataGridId="AccountPlans"
        alternateRowBg
        placeholder="No plans"
        title={`Active plans (${plans.length})`}
        autoHeight={false}
        search=""
        loading={potsLoading}
        hideFooter
        sx={{
          '& *': {
            textTransform: 'capitalize',
            textAlign: 'left',
            '-ms-overflow-style': 'none',
          },
          '& ::-webkit-scrollbar': {
            width: '0 !important',
          },
        }}
        data={plans}
        columns={
          djangoUser?.isUserDev || djangoUser?.isUserTester
            ? accountPlanColumnsWithDelete
            : accountPlanColumns
        }
        getDetailPanelContent={(params) => {
          const parent = params.row as Pot;
          const subplans = parent.children || [];
          if (
            parent.productName?.formattedName
              ?.toLocaleLowerCase()
              .includes('sipp drawdown') ||
            parent.productName?.formattedName
              ?.toLocaleLowerCase()
              .includes('sipp-drawdown')
          ) {
            return (
              <Box width="100%" sx={{overflow: 'hidden', height: 84, maxHeight: 84}}>
                <ListSubplans parent={parent} subplans={subplans} />
              </Box>
            );
          }
          return false;
        }}
        getDetailPanelHeight={(params) => {
          const parent = params.row as Pot;
          const planNeedsCrystallised = parent.productName?.formattedName
            ?.toLocaleLowerCase()
            .includes('sipp-drawdown');
          return planNeedsCrystallised ? 82 : 0;
        }}
        getRowClassName={(params) => {
          const row = params.row as Pot;
          if (row.parent?.id) {
            return 'child';
          }
          return 'parent';
        }}
        minHeight={false}
        rowHeight={40}
        onRowDoubleClick={(params) => {
          // navigate({pathname: `/contacts/${params.row.curoId}`});
        }}
      >
        <Box mt={1} mb={1} width="100%" justifyContent="space-between" display="flex">
          <Box flex={1} />
          <FeatureBoundary
            showErrorIfDenied={false}
            features={[GlobalPermissionEnum.can_access_instructions_FE]}
          >
            <Button
              eventName="Create instruction"
              variant="contained"
              color="primary"
              component={Link}
              to={`/account/${accountId}/${recommendsGridUrl}`}
            >
              Create instruction
            </Button>
          </FeatureBoundary>
        </Box>
      </SearchResults>
    </Grid>
  );
};
